import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { dayLabel, monthOf, payPeriodContaining, payPeriodEdges } from '@kronos/domain'
import type { DayLine, LocalDate, LocalMonth } from '@kronos/domain'
import { repository } from '../../data'
import { MonthCalendar } from '../calendar/MonthCalendar'
import { EmptyState, PageHeader, Panel, Section } from '../primitives'
import { DayCard } from './DayCard'
import { HoursCell, describeHours, hoursDayClass } from './HoursCell'
import { EarlierPeriods, HoursLegend, MonthTiles } from './HoursParts'
import { ShiftEditor } from './ShiftEditor'
import type { ShiftEdit } from './ShiftEditor'
import { deviceToday, problemOf } from './time'
import { useMonthHours } from './useMonthHours'

/**
 * My hours, as a month on a calendar: every day I worked with its time on it,
 * the days that need fixing in amber, and the line where one pay period ends
 * and the next begins. Tap a day and its shifts open underneath, with the
 * same Fix and Set clock-out the list used to have.
 *
 * Fixing your own hours needs nobody's permission. Every typed time is marked
 * on the report, which is the check; a form that has to be approved first is
 * a form people stop filling in, and then the hours are simply wrong.
 */
export function MyHoursView(): JSX.Element {
  const [month, setMonth] = useState<LocalMonth>(() => monthOf(deviceToday() as LocalDate))
  const [picked, setPicked] = useState<LocalDate | null>(null)
  const [editing, setEditing] = useState<ShiftEdit | null>(null)
  const hours = useMonthHours(month)
  const history = useQuery({ queryKey: ['shifts', 'history'], queryFn: () => repository.myHistory() })

  const addButton = <button onClick={() => setEditing({ kind: 'add' })}>+ Add a shift I forgot</button>

  return (
    <div className="page page--narrow hours-page">
      <PageHeader eyebrow="Paid twice a month" title="My hours" actions={addButton} />
      {hours.error ? <p className="critical">{problemOf(hours.error)}</p> : null}
      {hours.today ? (
        <Body
          month={month}
          today={hours.today}
          lines={hours.days}
          byDate={hours.byDate}
          periods={hours.periods}
          picked={picked}
          minMonth={earliest(history.data?.map((h) => h.period.from))}
          onMonth={(m) => {
            setMonth(m)
            setPicked(null)
          }}
          onPick={setPicked}
          onEdit={setEditing}
        />
      ) : hours.pending ? (
        <p className="muted">Loading…</p>
      ) : null}

      {history.data && hours.today ? (
        <EarlierPeriods
          history={history.data.filter((h) => h.period.from !== payPeriodContaining(hours.today as LocalDate).from)}
          month={month}
          onPick={(p) => {
            setMonth(monthOf(p.from))
            setPicked(null)
          }}
        />
      ) : null}

      {editing ? <ShiftEditor edit={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  )
}

function Body(props: {
  month: LocalMonth
  today: LocalDate
  lines: readonly DayLine[]
  byDate: ReadonlyMap<LocalDate, DayLine>
  periods: ReturnType<typeof useMonthHours>['periods']
  picked: LocalDate | null
  minMonth: LocalMonth | undefined
  onMonth: (m: LocalMonth) => void
  onPick: (d: LocalDate) => void
  onEdit: (e: ShiftEdit) => void
}): JSX.Element {
  const { month, today, lines, byDate } = props
  // The day on show: the one tapped, else today, else the last day worked this month.
  const shown =
    (props.picked && monthOf(props.picked) === month ? props.picked : null) ??
    (monthOf(today) === month ? today : null) ??
    lines.filter((d) => d.shifts.length > 0 && monthOf(d.date) === month).at(-1)?.date ??
    null
  const day = shown ? byDate.get(shown) : undefined

  return (
    <>
      <MonthTiles month={month} periods={props.periods} lines={lines} today={today} />

      <div className="section">
        <MonthCalendar
          label="Hours"
          month={month}
          onMonthChange={props.onMonth}
          today={today}
          minMonth={props.minMonth && props.minMonth < monthOf(today) ? props.minMonth : monthOf(today)}
          maxMonth={monthOf(today)}
          current={shown}
          isDisabled={(d) => d.date > today}
          dayClass={(d) => hoursDayClass(byDate.get(d.date))}
          cellClass={(d) => {
            const edge = payPeriodEdges(d.date)
            return `${edge.top ? 'edge-top' : ''} ${edge.left ? 'edge-left' : ''}`
          }}
          renderDay={(d) => <HoursCell day={byDate.get(d.date)} />}
          describeDay={(d) => describeHours(byDate.get(d.date))}
          onDayPress={props.onPick}
        />
        <HoursLegend />
      </div>

      {shown ? (
        <Section label={shown === today ? `Today · ${dayLabel(shown)}` : dayLabel(shown)}>
          <Panel>
            {day ? (
              <DayCard day={day} onEdit={props.onEdit} />
            ) : (
              <EmptyState
                icon="hours"
                title="Nothing clocked this day"
                action={<button onClick={() => props.onEdit({ kind: 'add' })}>Add a shift I forgot</button>}
              >
                {shown === today ? 'Start a shift from Today when you arrive.' : 'If you worked and forgot to clock in, add it here.'}
              </EmptyState>
            )}
          </Panel>
        </Section>
      ) : null}
    </>
  )
}

function earliest(dates: readonly string[] | undefined): LocalMonth | undefined {
  if (!dates || dates.length === 0) return undefined
  return monthOf([...dates].sort()[0] as LocalDate)
}
